import { useEffect, useRef } from "react";

interface UseGoogleDriveConnectReturnOptions {
  enabled?: boolean;
  onConnected?: () => void;
  onError?: (message: string) => void;
}

function stripConnectQueryParams(): { result: string | null; message: string | null } {
  const params = new URLSearchParams(window.location.search);
  const result = params.get("drive");
  const message = params.get("message");

  if (result !== "connected" && result !== "error") {
    return { result: null, message: null };
  }

  params.delete("drive");
  params.delete("message");
  const nextSearch = params.toString();
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}`;
  window.history.replaceState({}, "", nextUrl);

  return { result, message };
}

export function useGoogleDriveConnectReturn({
  enabled = true,
  onConnected,
  onError,
}: UseGoogleDriveConnectReturnOptions): void {
  const onConnectedRef = useRef(onConnected);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onConnectedRef.current = onConnected;
  }, [onConnected]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!enabled) return;

    const { result, message } = stripConnectQueryParams();
    if (result === "connected") {
      onConnectedRef.current?.();
      return;
    }

    if (result === "error") {
      onErrorRef.current?.(message || "Google Drive connection failed.");
    }
  }, [enabled]);
}
